import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Order } from '@/models/Order';
import {currency, formatDate} from '@/support/String';
import { OrderItem } from '@/models/OrderItem';
import { AxiosError } from 'axios';
import ErrorHandler from '@/support/ErrorHandler';
import { cloneDeep } from 'lodash';
import {
  formatDate as format,
} from "@/support/String";

@Component<OrderTable>({
  components: {
    //
  },
  filters : {
    currency(value: string) {
      return !value ? '' : currency(parseFloat(value).toFixed(2) as any);
    },
    formatDate(date: string) {
      if (!date) {
        return "";
      }

      return formatDate(date, 'dd-LL-yyyy H:m');
    }

  },

})
export default class OrderTable extends Vue {
  @Prop()
  protected order!: Order;

  @Prop({default: false})
  protected showUndispatched!: Boolean;

  protected totalOrderItems: OrderItem[] | null = [];
  protected returnedOrderItems: OrderItem[] | null = [];
  protected keptOrderItems: OrderItem[] | null = [];
  protected keptOrderItemsClone: OrderItem[] | null = [];

  protected newOrderItems: OrderItem[] | null = [];
  protected allocatedOrderItems: OrderItem[] | null = [];
  protected pickedOrderItems: OrderItem[] | null = [];
  protected dispatchedOrderItems: OrderItem[] | null = [];

  protected items: OrderItem[] = [];
  protected orderDiscount: string = '';
  protected canSaveOrderDiscount: boolean = false;
  protected isLoading: boolean = false;
  protected isEditingOrderItems: boolean = false;
  protected isUpdatingOrderItems: boolean = false;


  protected selectedDiscountType: string = 'percentage';
  protected discountTypes: any = [
    {
      name: '%',
      value: 'percentage',
    },
    {
      name: '€',
      value: 'value',
    },
  ]

  protected reducer = (accumulator: any, currentValue: any) => accumulator + currentValue;

  protected mounted() {
    this.initialize();
  }

  /**
   * finalizedOrder is true if this.order.status &&
   * this.order.status in checkout, invoiced, completed
   *
   * @readonly
   * @protected
   * @memberof OrderTable
   */
  protected get finalizedOrder() {

    return !!this.order.status &&
    [
      'checkout',
      'invoiced',
      'completed',
    ].includes(this.order.status);

  }

  protected initialize() {
    if (!this.order || !this.order.order_items) {
      return;
    }

    this.totalOrderItems = this.order.order_items as OrderItem[];

    this.totalOrderItems.forEach((orderItem: OrderItem) => {
      (orderItem as any)['canSave'] = false;
    })

    this.items = this.totalOrderItems;

    // Get the returned items, even when not finalized
    this.returnedOrderItems = this.order.order_items.filter((orderItem: OrderItem) => {
      return orderItem.returned;
    }).sort(function(a, b) {
      // Also sort, so the batches are in order
      // @ts-ignore
      return new Date(a.returned_at) - new Date(b.returned_at);
    });

    // Get the kept items, only when the order is finalized
    if (this.finalizedOrder) {
      this.keptOrderItems = this.order.order_items.filter((orderItem: OrderItem) => {
        return !orderItem.returned && orderItem.status === 'dispatched';
      });

      this.keptOrderItems.sort((a, b) => {
        // @ts-ignore
        return new Date(b.returned_at) - new Date(a.returned_at);
      })

      // Not sure why this is...
      if (this.order.status === 'checkout') {
        this.items = this.keptOrderItems;
      }
    }

    this.newOrderItems = this.order.order_items.filter((orderItem: OrderItem) => {
      return orderItem.status === 'new';
    });

    this.allocatedOrderItems = this.order.order_items.filter((orderItem: OrderItem) => {
      return orderItem.status === 'allocated';
    });

    this.pickedOrderItems = this.order.order_items.filter((orderItem: OrderItem) => {
      return orderItem.status === 'picked';
    });

    // Dispatched is a bit tricky, because it can appear in the dispatched and kept/returned at the same time
    // So, when the order is in (completed, checkout, invoiced), we don't show any dispatched items
    if (! this.finalizedOrder) {
      this.dispatchedOrderItems = this.order.order_items.filter((orderItem: OrderItem) => {
        return orderItem.status === 'dispatched' && !orderItem.returned;
      });
    }

    if (this.order.discount && this.order.discount_type) {
      this.orderDiscount = this.order.discount;
      this.selectedDiscountType = this.order.discount_type;
    }
  }

  protected get getTotalUnits() {
    if (!this.totalOrderItems || !this.keptOrderItems) {
      return;
    }

    return this.items.length;
  }

  protected get getSubTotal() {
    if (!this.totalOrderItems || !this.keptOrderItems) {
      return;
    }

    const subTotal = this.items.map((orderItem: OrderItem) => {
      if (orderItem.product) {
        return orderItem.price;
      }
    });

    return subTotal.reduce(this.reducer);
  }

  protected get getTotalVat() {
    if (!this.totalOrderItems || !this.keptOrderItems) {
      return;
    }

    const totalVat = this.items.map((orderItem: OrderItem) => {
      if (orderItem.product) {
        return orderItem.price as number * 0.21;
      }
    });

    return totalVat.reduce(this.reducer);
  }

  protected get getTotal() {
    if (!this.totalOrderItems || !this.keptOrderItems) {
      return;
    }

    const total = this.items.map((orderItem: OrderItem) => {
      if (orderItem.product) {
        return orderItem.price as number * 1.21;
      }
    });

    return total.reduce(this.reducer);
  }

  protected updateKeptOrderItems() {
    if (!this.order || !this.order.id || !this.keptOrderItems) {
      return;
    }

    let count = 0;

    this.keptOrderItems.forEach((orderItem: OrderItem) => {
      const payload = {
        discount: orderItem.discount,
        discount_type: 'percentage',
      };

      new OrderItem(orderItem)
      .updateOrderItem((this.order.id as string), (orderItem.id as string), payload)
      .then(() => {
        if (!this.keptOrderItems) {
          return;
        }

        count += 1;
        if (count === this.keptOrderItems.length) {
          if (!this.selectedDiscountType && !this.order && !this.orderDiscount) {
            return;
          }

          const payload = {
            discount: this.orderDiscount.replace(/,/g, '.'),
            discount_type: this.selectedDiscountType,
          };

          new Order(this.order)
          .updateOrder((this.order.id as string), payload)
          .then(() => {
            this.$emit('orderItemUpdated');
            this.isEditingOrderItems = false;
          })
          .catch((error: AxiosError) => {
            ErrorHandler.network(error);
          });
        }
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
        this.isLoading = false;
      });
    });
  }

  protected resetKeptOrderItems() {
    if (!this.keptOrderItemsClone) {
      return;
    }

    this.keptOrderItems = cloneDeep(this.keptOrderItemsClone);
    this.keptOrderItemsClone = null;
  }

  protected getTotalItems(items: OrderItem[] = [])
  {
      return items.reduce((a, b: OrderItem) => {
          if (! b ||! b.quantity) {
            return a;
          }
          return a + b.quantity;
      }, 0);
  }

  protected saveOrderDiscount(order: Order) {

  }

  /**
   * Is this order item returned in a new batch?
   * Groups are returned item batches
   *
   * @param index
   */
  protected isDifferentBatch(index: number) {
    if (index === 0) {
      return true;
    }

    if (! this.returnedOrderItems || ! this.returnedOrderItems[index - 1]) {
      return false;
    }

    const previousDate = new Date(this.returnedOrderItems[index -1].returned_at);
    const currentDate = new Date(this.returnedOrderItems[index].returned_at);

    // @ts-ignore
    return Math.abs(currentDate - previousDate) >= 60 * 1000; // If it's within a minute of eachother, we can assume it's the same batch
  }

  @Watch('orderDiscount')
  @Watch('selectedDiscountType')
  protected orderDiscountChanged() {
    if (this.order.discount_type !== this.selectedDiscountType) {
      this.canSaveOrderDiscount = true;
      return;
    }
    if (this.order.discount !== this.orderDiscount) {
      this.canSaveOrderDiscount = true;
      return;
    }
  }

  @Watch('isEditingOrderItems')
  protected editingChanged(isEditing: boolean) {
    if (isEditing) {
      this.keptOrderItemsClone = cloneDeep(this.keptOrderItems);
    }
  }
}
